import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowDown,
  ArrowRight,
  Globe,
  Palette,
  Terminal,
  Lightbulb,
  Megaphone,
  Search,
  BarChart3,
  Shield,
  Bot,
  Calendar,
  Database,
  Store,
  WholeWord as WordPress,
  Settings,
  Sparkles,
  CheckCircle2,
  ChevronDown
} from 'lucide-react';
import { Link } from 'react-router-dom';
import LatestNews from '../components/LatestNews';
import SEO from '../components/SEO';

const Home = () => {
  const { t } = useTranslation();

  const services = [
    { 
      icon: Globe, 
      title: t('services.webDevelopment.title'),
      description: t('home.services.webDevelopment.description'),
      path: '/thjonusta/vefsidugerd',
      features: [
        t('home.services.webDevelopment.features.responsive'),
        t('home.services.webDevelopment.features.seo'),
        t('home.services.webDevelopment.features.fast')
      ]
    },
    { 
      icon: Palette, 
      title: t('services.webDesign.title'),
      description: t('home.services.webDesign.description'),
      path: '/thjonusta/vefhonnun',
      features: [
        t('home.services.webDesign.features.ux'),
        t('home.services.webDesign.features.branding'),
        t('home.services.webDesign.features.prototype')
      ]
    },
    { 
      icon: Terminal, 
      title: t('services.webProgramming.title'),
      description: t('home.services.webProgramming.description'),
      path: '/thjonusta/vefthroun',
      features: [
        t('home.services.webProgramming.features.custom'),
        t('home.services.webProgramming.features.api'),
        t('home.services.webProgramming.features.programming')
      ]
    },
    { 
      icon: Search, 
      title: t('services.seo.title'),
      description: t('home.services.seo.description'),
      path: '/thjonusta/leitarvelabestun',
      features: [
        t('home.services.seo.features.keywords'),
        t('home.services.seo.features.technical'),
        t('home.services.seo.features.content')
      ]
    },
    { 
      icon: Megaphone, 
      title: t('services.marketing.title'),
      description: t('home.services.marketing.description'),
      path: '/thjonusta/markadssetning',
      features: [
        t('home.services.marketing.features.social'),
        t('home.services.marketing.features.ads'),
        t('home.services.marketing.features.content')
      ]
    },
    { 
      icon: BarChart3, 
      title: t('services.analytics.title'),
      description: t('home.services.analytics.description'),
      path: '/thjonusta/greiningar',
      features: [
        t('home.services.analytics.features.google'),
        t('home.services.analytics.features.heatmap'),
        t('home.services.analytics.features.testing')
      ]
    }
  ];

  const solutions = [
    { 
      icon: Bot, 
      title: t('solutions.ai.title'),
      description: t('solutions.ai.description'),
      path: '/lausnir/gervigreind',
      features: [
        t('solutions.ai.features.chatbot'),
        t('solutions.ai.features.automation'),
        t('solutions.ai.features.analysis')
      ]
    },
    { 
      icon: Calendar, 
      title: t('solutions.booking.title'),
      description: t('solutions.booking.description'),
      path: '/lausnir/bokunarkerfi',
      features: [
        t('solutions.booking.features.online'),
        t('solutions.booking.features.payment'),
        t('solutions.booking.features.reminders')
      ]
    },
    { 
      icon: Database, 
      title: t('solutions.subscription.title'),
      description: t('solutions.subscription.description'),
      path: '/lausnir/askriftarkerfi',
      features: [
        t('solutions.subscription.features.management'),
        t('solutions.subscription.features.payment'),
        t('solutions.subscription.features.users')
      ]
    },
    { 
      icon: Store, 
      title: t('solutions.ecommerce.title'),
      description: t('solutions.ecommerce.description'),
      path: '/lausnir/netverslun',
      features: [
        t('solutions.ecommerce.features.products'),
        t('solutions.ecommerce.features.payment'),
        t('solutions.ecommerce.features.shipping')
      ]
    },
    { 
      icon: WordPress, 
      title: t('solutions.wordpressExpress.title'),
      description: t('solutions.wordpressExpress.description'),
      path: '/lausnir/wordpress-express',
      features: [
        t('solutions.wordpressExpress.features.price'),
        t('solutions.wordpressExpress.features.pages'),
        t('solutions.wordpressExpress.features.theme')
      ]
    },
    { 
      icon: Settings, 
      title: t('solutions.api.title'),
      description: t('solutions.api.description'),
      path: '/lausnir/api-tengingar',
      features: [
        t('solutions.api.features.custom'),
        t('solutions.api.features.integration'),
        t('solutions.api.features.security')
      ]
    }
  ];

  const customerLogos = [
    {
      name: 'Nathan og Olsen',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460377/logo-nathan_bkgppi.png'
    },
    {
      name: 'Tjarnarskóli',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460377/tjarnarskoli-logo_lwtekp.png'
    },
    {
      name: 'Rauði krossinn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460378/logo-raudikross_ee5ean.png'
    },
    {
      name: 'Ásbjörn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460376/logo-asbjorn_cr8ffx.png'
    },
    {
      name: 'Landsbókasafn',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460376/logo-lbs_bujuyr.png'
    },
    {
      name: 'Hjallastefnan',
      logo: 'https://res.cloudinary.com/djimcqajg/image/upload/c_scale,h_100,w_auto/v1741460376/hjallastefnan-logo_qnmhhl.png'
    }
  ];

  const stats = [
    { number: '500+', label: t('home.stats.clients') },
    { number: '600+', label: t('home.stats.projects') },
    { number: '99,9%', label: t('home.stats.uptime') },
    { number: '24/7', label: t('home.stats.support') }
  ];

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 50
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const handleScrollDown = () => {
    const servicesSection = document.getElementById('services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO 
        title={t('site.homeTitle')}
        description={t('site.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-[80vh] bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <motion.div 
          className="absolute inset-0 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute w-[500px] h-[500px] bg-[#EA295A]/5 rounded-full blur-3xl -top-20 -right-20" />
          <div className="absolute w-[300px] h-[300px] bg-blue-500/5 rounded-full blur-2xl top-40 -right-10" />
          <div className="absolute w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-2xl bottom-0 -right-20" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="relative">
            <motion.div 
              className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              <div>
                <motion.div
                  variants={itemVariants}
                  className="inline-flex items-center px-4 py-2 bg-[#EA295A]/5 rounded-full mb-6"
                >
                  <Sparkles className="w-4 h-4 text-[#EA295A] mr-2" />
                  <span className="text-[#EA295A] text-sm font-medium">{t('home.hero.badge')}</span>
                </motion.div>

                <motion.h1
                  variants={itemVariants}
                  className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
                  dangerouslySetInnerHTML={{ __html: t('home.hero.title') }}
                />

                <motion.p
                  variants={itemVariants}
                  className="text-xl text-gray-600 mb-8 leading-relaxed"
                >
                  {t('home.hero.description')}
                </motion.p>

                <motion.div
                  variants={itemVariants}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/fa-tilbod">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center group"
                    >
                      {t('common.getQuote')}
                      <motion.span
                        className="ml-2"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowRight className="transform group-hover:translate-x-1 transition-transform" />
                      </motion.span>
                    </motion.button>
                  </Link>
                  <Link to="/thjonusta">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all border border-gray-200 shadow-sm"
                    >
                      {t('home.hero.exploreServices')}
                    </motion.button>
                  </Link>
                </motion.div>
              </div>

              <div className="relative">
                <motion.div
                  className="relative z-10 rounded-3xl overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <img 
                    src="https://res.cloudinary.com/djimcqajg/image/upload/v1741454400/vs-office_ah094w.jpg"
                    alt={t('home.hero.imageAlt')}
                    className="w-full h-[600px] object-cover"
                  />
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              className="absolute left-1/2 -translate-x-1/2 bottom-12 z-20 cursor-pointer"
              onClick={handleScrollDown}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <div className="flex flex-col items-center">
                <motion.div
                  className="flex flex-col items-center"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
                    <ArrowDown className="w-6 h-6 text-[#EA295A]" />
                    <span className="sr-only">{t('home.hero.scrollDown')}</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div 
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        id="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center"
                variants={itemVariants}
              >
                <motion.div 
                  className="text-4xl font-bold text-[#EA295A] mb-2"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('home.clients.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.clients.description')}
            </p>
          </motion.div>

          <div className="relative overflow-hidden">
            <motion.div 
              className="py-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
                {customerLogos.map((logo, index) => (
                  <motion.div
                  key={index}
                  className="flex items-center justify-center"
                  whileHover={{ y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="bg-white rounded-lg shadow-sm p-3 h-20 w-36 flex items-center justify-center hover:shadow-md transition-all duration-300">
                    <img
                      src={logo.logo}
                      alt={logo.name} 
                      className="max-h-14 max-w-full object-contain transition-all duration-300"
                    />
                  </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/verkefni" className="inline-flex items-center text-[#EA295A] font-medium hover:text-[#d01d4c]">
              {t('common.seeAllProjects')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div 
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('common.services')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.services.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                className="bg-gray-50 rounded-xl p-8"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="bg-[#EA295A]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.path}
                  className="mt-6 inline-flex items-center text-[#EA295A] hover:text-[#d01d4c] font-medium"
                >
                  {t('common.readMore')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="py-24 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('common.solutions')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.solutions.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div 
                key={index} 
                className="bg-white rounded-xl p-8 shadow-sm"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="bg-[#EA295A]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <solution.icon className="w-7 h-7 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-3">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={solution.path}
                  className="mt-6 inline-flex items-center text-[#EA295A] hover:text-[#d01d4c] font-medium"
                >
                  {t('common.readMore')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <LatestNews />

      <motion.div 
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold text-white mb-6"
            >
              {t('home.cta.title')}
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              {t('home.cta.description')}
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  {t('common.getQuote')}
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Home;